import React, { useLayoutEffect, useRef, useState } from 'react'
import { AppButton } from '@easyllama/ui-components'
import PlansDialog from '~/organisms/Billing/PlansDialog'
import ContactSalesDialog from '~/molecules/Billing/ContactSalesDialog'
import { AppContext, useApp } from '~/context/appContext'
import { sanitizeHtml } from '~/utils/commonUtils'
import { ANNOUNCEMENT_EVENTS } from '~/service/Announcements/fetchApiClient'
import { useAnnouncementEvents } from './useAnnouncementEvents'
import { useAnnouncementBilling } from './useAnnouncementBilling'
import {
  ANNOUNCEMENT_BODY_ALLOWED_ATTR,
  ANNOUNCEMENT_BODY_ALLOWED_TAGS,
  ANNOUNCEMENT_BODY_SANITIZE_OPTIONS,
  UPGRADE_NOW_TYPE,
  ctaHref,
  isModifiedClick,
} from './announcementBody'
import { LlamaClose } from '@easyllama/ui-components/icons'
import { companyBillingPath } from '~/routes'

const CTA_BANNER_OFFSET_CSS_VAR = '--cta-banner-offset'
// Design height, used until the bar can be measured and wherever it cannot be (server
// render, jsdom).
const CTA_BANNER_HEIGHT_PX = 80

const CTA_WRAP_CLASSES = 'max-w-full !h-auto min-h-[38px] !whitespace-normal'

const DEFAULT_GRADIENT_FROM = '#ff9615'
const DEFAULT_GRADIENT_TO = '#ffe4b4'

// Exported to be assertable: jsdom cannot parse a gradient and drops the declaration, so
// there is nothing left on the element to test against.
export const announcementGradient = (appearance) =>
  `linear-gradient(to right, ${appearance?.gradientFrom || DEFAULT_GRADIENT_FROM}, ${appearance?.gradientTo || DEFAULT_GRADIENT_TO})`

const InAppAnnouncementBanner = ({
  placement,
  headline,
  bodyHtml,
  contactCta,
  primaryCta,
  secondaryCta,
  appearance,
  eventsUrl,
  renderToken,
  preview = false
}) => {
  const appContext = useApp()
  const { company } = appContext
  const [plansDialogOpen, setPlansDialogOpen] = useState(false)
  const [contactSalesOpen, setContactSalesOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const barRef = useRef(null)

  const isTopBar = placement === 'top_bar'
  const isVisible = isTopBar && Boolean(headline) && !dismissed
  const isUpgradeNowCta = primaryCta?.type === UPGRADE_NOW_TYPE
  // Null for anything we will not follow, which also drops the control - see ctaHref.
  const primaryHref = ctaHref(primaryCta?.url)
  const secondaryHref = ctaHref(secondaryCta?.url)

  const report = useAnnouncementEvents({ eventsUrl, renderToken, preview, viewOnMount: true })
  const billingContextValue = useAnnouncementBilling(appContext, { enabled: isUpgradeNowCta, renderToken })

  const handleContactClick = (event) => {
    // A modified click is a request for a new tab - the point of this being an anchor.
    if (isModifiedClick(event)) return

    event.preventDefault()
    setContactSalesOpen(true)
  }

  const handleClose = () => {
    setDismissed(true)
    report(ANNOUNCEMENT_EVENTS.CLOSED)
  }

  // Not awaited: both CTAs stay real links, and the event fetch sends keepalive.
  const handlePrimaryNavigate = () => report(ANNOUNCEMENT_EVENTS.PRIMARY_CTA)

  // Not a snooze: the server files a navigating secondary as secondary_clicked_at.
  const handleSecondaryNavigate = () => report(ANNOUNCEMENT_EVENTS.SECONDARY_CTA)

  // primary_cta hides the bar for the session; only a conversion retires it (ENG-11753
  // Decision 6). Chained for the same reason as the modal's - see InAppAnnouncementModal.
  const handleUpgradeClick = () => {
    report(ANNOUNCEMENT_EVENTS.PRIMARY_CTA).then(() => report(ANNOUNCEMENT_EVENTS.BILLING_VIEWED))
    setPlansDialogOpen(true)
  }

  // Everything that makes room for the bar - AdminLayout's margin, the legacy
  // .wrapper__inner margin, the sidebar's top/height - reads this one variable, so they move
  // and restore together without knowing about each other.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined

    const publish = (px) => document.documentElement.style.setProperty(CTA_BANNER_OFFSET_CSS_VAR, `${px}px`)

    if (!isVisible) {
      publish(0)
      return undefined
    }

    const measure = () => publish(barRef.current?.getBoundingClientRect().height || CTA_BANNER_HEIGHT_PX)
    measure()

    // Absent in jsdom and on the server; the measure above covers the steady state, this
    // only keeps up with copy reflowing on resize.
    const observer = typeof ResizeObserver === 'undefined' || !barRef.current
      ? null
      : new ResizeObserver(measure)
    observer?.observe(barRef.current)

    return () => {
      observer?.disconnect()
      publish(0)
    }
  }, [isVisible])

  const planUpgraded = () => setDismissed(true)

  const onViewDetails = () => {
    window.location.href = companyBillingPath(company?.id)
  }

  return (
    <>
      {/* Keys the Bootstrap reset in shared/_in_app_announcement_styling.html.erb. Unconditional,
          not tied to isVisible: a billing success closes the bar (setDismissed above) while
          PlansDialog/ChangePlanDialog - a portal outside this tree - can still be open showing
          its success step, and needs the scope to keep matching. Same marker and same reasoning
          as InAppAnnouncementModal.jsx. */}
      <span className='announcement-modal-root' aria-hidden='true' hidden />

      {isVisible && (
        // Arbitrary values, not py-5/px-4/gap-4: bootstrap.css is loaded on every legacy
        // admin page and defines those same names !important at a different scale, which no
        // cascade-layer reset can outrank.
        <div
          ref={barRef}
          className={`announcement-root fixed inset-x-0 top-0 z-40 max-w-screen flex flex-wrap py-[21px] px-[42px] items-center justify-between gap-[16px] text-black${preview ? ' pointer-events-none' : ''}`}
          style={{ backgroundImage: announcementGradient(appearance) }}
        >
          {/* basis, not flex-1: flex-wrap breaks the row on an item's hypothetical size, so a
              column free to shrink to nothing would never push the actions onto their own row. */}
          <div className='grow shrink basis-[520px] min-w-0'>
            {/* The clamp needs an element of its own: line-clamp sets display:-webkit-box, and
                a flex child's display is blockified, which would discard it. */}
            <div className='line-clamp-5 md:line-clamp-2 text-base [&_p]:inline [&_a]:font-medium [&_a]:underline [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-5 [&_ol]:ml-5 [&_h3]:font-bold [&_h4]:font-bold'>
              {headline && <span className='font-bold'>{headline} </span>}
              {bodyHtml && (
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(bodyHtml, ANNOUNCEMENT_BODY_ALLOWED_TAGS, ANNOUNCEMENT_BODY_ALLOWED_ATTR, ANNOUNCEMENT_BODY_SANITIZE_OPTIONS),
                  }}
                />
              )}
              {/* Inside the clamped block so it reads as the last sentence of the copy; outside
                  it the block fills the column and the link lands over on the far right. The
                  separating space is explicit: with [&_p]:inline the only other one is whatever
                  the author left before the link, which Rails passes through when it lifts the
                  anchor out - and which is missing entirely if the link was written hard against
                  the sentence or as its own paragraph. A double space collapses to one. */}
              {contactCta?.label && contactCta?.url && (
                <>
                  {' '}
                  <a href={contactCta.url} onClick={handleContactClick} className='underline whitespace-nowrap'>
                    {contactCta.label}
                  </a>
                </>
              )}
            </div>
          </div>

          <div className='flex flex-wrap min-w-0 max-w-full items-center gap-2'>
            {/* asChild so a destination is a real <a>: context menu, middle click, "open in
                new tab", announced as a link - none of which window.location.href gives.
                target=_blank because this points off the dashboard (docs, blog, etc.) - a
                default click must not navigate the admin away from what they were doing
                (ENG-11905). noopener/noreferrer: the new tab gets no window.opener handle
                back to this page. */}
            {secondaryCta?.label && secondaryHref && (
              <AppButton
                asChild
                variant='ghost'
                className={`border border-black hover:bg-transparent ${CTA_WRAP_CLASSES}`}
              >
                <a
                  href={secondaryHref}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={handleSecondaryNavigate}
                >
                  {secondaryCta.label}
                </a>
              </AppButton>
            )}
            {/* A billing_modal CTA opens the dialog in place, so it stays a button - there is
                no destination to open. */}
            {primaryCta?.label && primaryHref && (
              isUpgradeNowCta ? (
                <AppButton onClick={handleUpgradeClick} className={`!bg-black hover:!text-llama-purple-4 ${CTA_WRAP_CLASSES}`}>
                  {primaryCta.label}
                </AppButton>
              ) : (
                <AppButton asChild className={`!bg-black hover:!text-llama-purple-4 ${CTA_WRAP_CLASSES}`}>
                  <a href={primaryHref} onClick={handlePrimaryNavigate}>{primaryCta.label}</a>
                </AppButton>
              )
            )}

            <div
              className='absolute top-1 right-1 cursor-pointer'
              role='button'
              aria-label='Dismiss announcement'
              onClick={handleClose}
            >
              <LlamaClose className='size-4.5' />
            </div>
          </div>
        </div>
      )}

      {isUpgradeNowCta && company && (
        <AppContext.Provider value={billingContextValue}>
          <PlansDialog
            open={plansDialogOpen}
            onOpenChange={setPlansDialogOpen}
            onSuccess={planUpgraded}
            onViewDetails={onViewDetails}
          />
        </AppContext.Provider>
      )}

      {contactSalesOpen && company && (
        <ContactSalesDialog
          open={contactSalesOpen}
          handleOpenChange={setContactSalesOpen}
        />
      )}
    </>
  )
}

export default InAppAnnouncementBanner
